import React from 'react';
import { all, call, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { request } from '@wix/da-http-client/pkg/request';
import { groupJoinFail, groupJoinPending, groupJoinSuccess, groupLeaveFail, groupLeaveSuccess, requestJoinGroup, requestLeaveGroup } from './actions';
import { Url, URLS, USER_PROFILE_BACKROOM_SECTIONS } from '@wix/da-url';
import { LeaveGroup as LeaveGroupIcon } from '@wix/da-ds/pkg/Icons/Confirmations';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications/redux/types';
import { confirmNo, confirmYes, pushGlobalConfirmationModal, pushModal } from '../../Modals/redux/actionCreators';
import { getUserWatchState } from '../../WatchButton/redux/selectors';
import i18next from 'i18next';
import { userSchema } from '../normalizr/schemas/user';
import { normalize } from '../normalizr';
import { putEntities } from '../entities/actions';
import { ModalType } from '../../Modals/redux/types';
function* handleClosedGroupJoinRequest(group) {
    const groupWatchState = yield select(getUserWatchState, group);
    if (groupWatchState.isWatching) {
        yield put(addNotification(NotificationType.Generic, {
            message: i18next.t('group.join.errors.closed', {
                groupname: group.username
            }),
            isError: true,
            cardProps: {
                multiLine: true,
                duration: 5000
            }
        }));
        return;
    }
    yield put(pushModal(ModalType.GROUP_CLOSED_WATCH, {
        params: {
            group
        },
        wrapInModal: false
    }));
}
function* handleGroupJoinRequest({ payload: { group } }) {
    var _group_extendedGroup_apps_join, _group_extendedGroup_apps, _group_extendedGroup, _group_extendedGroup_apps1, _group_extendedGroup1, _result_thisGroup_extendedGroup;
    if (!((_group_extendedGroup = group.extendedGroup) === null || _group_extendedGroup === void 0 ? void 0 : (_group_extendedGroup_apps = _group_extendedGroup.apps) === null || _group_extendedGroup_apps === void 0 ? void 0 : (_group_extendedGroup_apps_join = _group_extendedGroup_apps.join) === null || _group_extendedGroup_apps_join === void 0 ? void 0 : _group_extendedGroup_apps_join.canJoin)) {
        yield call(handleClosedGroupJoinRequest, group);
        return;
    }
    const roleId = (_group_extendedGroup1 = group.extendedGroup) === null || _group_extendedGroup1 === void 0 ? void 0 : (_group_extendedGroup_apps1 = _group_extendedGroup1.apps) === null || _group_extendedGroup_apps1 === void 0 ? void 0 : _group_extendedGroup_apps1.join.intoRoleid;
    if (!roleId) {
        return;
    }
    const data = {
        groupid: group.userId,
        roleid: roleId
    };
    const result = yield call(request, {
        method: 'post',
        url: 'group/join',
        data
    });
    if (!result || !result.success || !result.thisGroup) {
        yield all([
            put(addNotification(NotificationType.Generic, {
                message: result && result.errorDescription,
                messageLocaleKey: result ? `group.join.errors.${result.errorDescription}` : 'group.join.errors.generic',
                isError: true,
                cardProps: {
                    multiLine: true,
                    duration: 5000
                }
            })),
            put(groupJoinFail(group, result && result.errorDescription || i18next.t('group.join.errors.generic')))
        ]);
        return;
    }
    const { entities } = normalize(result.thisGroup, userSchema);
    yield put(putEntities({
        entities
    }));
    if ((_result_thisGroup_extendedGroup = result.thisGroup.extendedGroup) === null || _result_thisGroup_extendedGroup === void 0 ? void 0 : _result_thisGroup_extendedGroup.isIngroup) {
        yield put(groupJoinSuccess(result.thisGroup));
        return;
    }
    yield all([
        put(groupJoinPending(result.thisGroup)),
        put(pushModal(ModalType.GROUP_JOIN_PENDING, {
            params: {
                group
            },
            wrapInModal: false
        }))
    ]);
}
function* handleGroupLeaveRequest({ payload: { group } }) {
    var _group, _extendedGroup_apps_join, _extendedGroup_apps, _extendedGroup, _extendedGroup1;
    const extendedGroup = (_group = group) === null || _group === void 0 ? void 0 : _group.extendedGroup;
    const canLeave = (_extendedGroup = extendedGroup) === null || _extendedGroup === void 0 ? void 0 : (_extendedGroup_apps = _extendedGroup.apps) === null || _extendedGroup_apps === void 0 ? void 0 : (_extendedGroup_apps_join = _extendedGroup_apps.join) === null || _extendedGroup_apps_join === void 0 ? void 0 : _extendedGroup_apps_join.canLeave;
    const inGroup = (_extendedGroup1 = extendedGroup) === null || _extendedGroup1 === void 0 ? void 0 : _extendedGroup1.isIngroup;
    if (inGroup && !canLeave) {
        yield put(pushGlobalConfirmationModal({
            confirmBtnLabelTransKey: 'group.leave.founder.button.accept',
            textTransKey: 'group.leave.founder.error.body',
            titleTransKey: 'group.leave.founder.error.heading',
            confirmBtnType: 'danger',
            translationOptions: {
                groupname: group.username
            }
        }));
        const { redirect } = yield race({
            redirect: take(confirmYes),
            cancel: take(confirmNo)
        });
        if (redirect) {
            // redirect to group backroom
            window.location.href = Url.groupBackroomLink(group.username, USER_PROFILE_BACKROOM_SECTIONS.ROOT, '', {
                flow: 'founder-leave'
            });
            return;
        }
        return;
    }
    // Confirm Leaving
    yield put(pushGlobalConfirmationModal({
        cancelBtnLabelTransKey: 'common.cancel',
        confirmBtnLabelTransKey: 'group.leave.confirm.button.leave',
        textTransKey: 'group.leave.confirm.body',
        titleTransKey: 'group.leave.confirm.heading',
        confirmBtnType: 'default',
        translationOptions: {
            groupname: group.username
        },
        icon: /*#__PURE__*/ React.createElement(LeaveGroupIcon, null)
    }));
    const { no } = yield race({
        yes: take(confirmYes),
        no: take(confirmNo)
    });
    if (no) {
        return;
    }
    const data = {
        groupid: group.userId
    };
    const result = yield call(request, {
        method: 'post',
        url: 'group/leave',
        data
    });
    if (!result || !result.success || !result.thisGroup) {
        yield all([
            put(addNotification(NotificationType.Generic, {
                message: result && result.errorDescription,
                messageLocaleKey: result ? `group.leave.errors.${result.errorDescription}` : 'group.leave.errors.generic',
                isError: true,
                cardProps: {
                    multiLine: true,
                    duration: 5000
                }
            })),
            put(groupLeaveFail(group, result && result.errorDescription || i18next.t('group.leave.errors.generic')))
        ]);
        return;
    }
    const { entities } = normalize(result.thisGroup, userSchema);
    yield all([
        put(putEntities({
            entities
        })),
        put(groupLeaveSuccess(result.thisGroup))
    ]);
    // Redirect to rootpage
    window.location.assign(URLS.ROOT);
}
export default function* groupsSagaRoot() {
    yield all([
        takeEvery(requestJoinGroup, handleGroupJoinRequest),
        takeEvery(requestLeaveGroup, handleGroupLeaveRequest)
    ]);
}
